import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { Dropdown } from "../../../components/form-components/Dropdown";
import Tooltip from "../../../components/form-components/Tooltip";
import { getSourceTypes, getCategories } from "../taxonomyApi";

const ANY = { value: "uncategorized", label: "Any type — capture all cost not matched elsewhere" };

// Tooltip copy is finance-grade — explains what the badge means *for allocation*, not for
// the data model. Confidence is context-dependent so a "medium" badge actually tells the
// user *why* they should care, instead of leaving them to guess what the rating means.
const SHARED_TIP = "This spend supports multiple teams or workloads (e.g. shared infra, observability). It usually needs a split rule rather than a single owner.";
const DIRECT_TIP = "This spend has a clear owner — one project, one team, one workload. It can be allocated to a single owner without a split rule.";
const CONFIDENCE_TIPS = {
  high: "The cost-base rows for this source consistently match this type. You can trust the classification without a manual rule.",
  medium: "Most cost-base rows for this source match this type, but some don't fit cleanly. Usually reliable; spot-check before assigning a large allocation.",
  low: "The cost-base rows for this source match this type inconsistently. Worth a manual review or a tighter rule before relying on the allocation.",
};

const SourceTypePicker = ({ value, onChange }) => {
  const typesQ = useQuery(["taxonomy-source-types"], getSourceTypes);
  const catsQ = useQuery(["taxonomy-categories"], getCategories);

  const cats = useMemo(() => catsQ.data?.categories || [], [catsQ.data]);
  const types = useMemo(() => typesQ.data?.source_types || [], [typesQ.data]);
  const loading = typesQ.isLoading || catsQ.isLoading;

  // react-select grouped options: "Any type" first, then one group per non-empty category.
  const options = useMemo(() => {
    const groups = cats
      .map((c) => ({
        label: c.display_name,
        options: types
          .filter((t) => t.category_id === c.category_id && t.source_type_id !== "uncategorized")
          .map((t) => ({ value: t.source_type_id, label: t.display_name, subtitle: c.display_name })),
      }))
      .filter((g) => g.options.length > 0);
    return [ANY, ...groups];
  }, [cats, types]);

  const flat = useMemo(() => options.flatMap((o) => (o.options ? o.options : [o])), [options]);
  const selectedOption = flat.find((o) => o.value === value) || ANY;

  const selectedType = types.find((t) => t.source_type_id === value);
  const selectedCat = selectedType ? cats.find((c) => c.category_id === selectedType.category_id) : null;
  const isAny = !value || value === "uncategorized";

  return (
    <div>
      <label htmlFor="source-type" className="block text-xs font-medium text-text-2 mb-1">What is this source?</label>
      {loading ? (
        <div className="w-full border border-border rounded-ctrl px-3 py-1.5 text-sm bg-surface-2 text-text-3">Loading types…</div>
      ) : (
        <Dropdown
          inputId="source-type"
          options={options}
          selectedValue={selectedOption}
          setSelectedValue={(opt) => opt && onChange(opt.value)}
          fullWidth
          twoLine
        />
      )}
      <div className="mt-2 space-y-1.5">
        {isAny ? (
          <p className="text-xs text-text-2">Captures all cost that isn't matched by another source.</p>
        ) : (
          <>
            {selectedCat && (
              <p className="text-xs text-text-2">
                {selectedCat.description
                  ? `${selectedCat.display_name} — ${selectedCat.description}`
                  : selectedCat.display_name}
              </p>
            )}
            {selectedType && (() => {
              const shared = selectedType.default_shared_flag;
              const sharedLabel = shared ? "Shared across teams" : "Directly attributable";
              const sharedTip = shared ? SHARED_TIP : DIRECT_TIP;
              const conf = (selectedType.default_confidence || "medium").toLowerCase();
              const confTip = CONFIDENCE_TIPS[conf] || CONFIDENCE_TIPS.medium;
              return (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span
                    className="text-[11px] px-1.5 py-0.5 rounded-full bg-surface-2 text-text-2 cursor-help"
                    data-tooltip-id="instantTooltip"
                    data-tooltip-header={sharedLabel}
                    data-tooltip-content={sharedTip}
                  >
                    {sharedLabel}
                  </span>
                  <span
                    className="text-[11px] px-1.5 py-0.5 rounded-full bg-surface-2 text-text-2 cursor-help"
                    data-tooltip-id="instantTooltip"
                    data-tooltip-header={`${conf} confidence`}
                    data-tooltip-content={confTip}
                  >
                    {conf} confidence
                  </span>
                </div>
              );
            })()}
          </>
        )}
      </div>
      <Tooltip />
    </div>
  );
};

SourceTypePicker.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

export default SourceTypePicker;
